export const SHOWCASE_STATUSES = [
    "awaiting_upload",
    "transcoding",
    "ready",
    "failed",
];
export const SHOWCASE_DEFAULT_ACCENT = "#7c9cff";
export const SHOWCASE_NOTIFY_COMPLETION_PCT = 75;
export const SHOWCASE_MAX_SECONDS = 3 * 60 * 60;
const SLUG_RE = /^[A-Za-z0-9_-]{8,32}$/;
export function isShowcaseSlug(s) {
    return typeof s === "string" && SLUG_RE.test(s);
}
const REF_RE = /^[a-z0-9][a-z0-9._-]{0,47}$/;
export function normaliseRef(raw) {
    if (typeof raw !== "string")
        return null;
    const r = raw.trim().toLowerCase();
    return REF_RE.test(r) ? r : null;
}
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
export function isHexColor(s) {
    return typeof s === "string" && HEX_RE.test(s);
}
export function isSafeHttpUrl(s) {
    if (typeof s !== "string" || s.length > 2048)
        return false;
    try {
        const u = new URL(s);
        return u.protocol === "https:" || u.protocol === "http:";
    }
    catch {
        return false;
    }
}
export function newBits(seconds) {
    const n = Math.max(0, Math.min(SHOWCASE_MAX_SECONDS, Math.ceil(seconds)));
    return new Uint8Array(Math.ceil(n / 8));
}
export function setBit(bits, i) {
    if (i < 0 || i >= SHOWCASE_MAX_SECONDS)
        return bits;
    const byte = i >> 3;
    const out = new Uint8Array(Math.max(bits.length, byte + 1));
    out.set(bits);
    out[byte] |= 1 << (i & 7);
    return out;
}
export function hasBit(bits, i) {
    const byte = i >> 3;
    return byte < bits.length && (bits[byte] & (1 << (i & 7))) !== 0;
}
export function countBits(bits) {
    let n = 0;
    for (let i = 0; i < bits.length; i++) {
        let b = bits[i];
        while (b) {
            b &= b - 1;
            n++;
        }
    }
    return n;
}
export function orBits(a, b) {
    var _a, _b;
    const out = new Uint8Array(Math.max(a.length, b.length));
    for (let i = 0; i < out.length; i++)
        out[i] = ((_a = a[i]) !== null && _a !== void 0 ? _a : 0) | ((_b = b[i]) !== null && _b !== void 0 ? _b : 0);
    return out;
}
export function newlySet(prev, next) {
    var _a;
    const out = [];
    for (let byte = 0; byte < next.length; byte++) {
        const fresh = next[byte] & ~((_a = prev[byte]) !== null && _a !== void 0 ? _a : 0);
        if (!fresh)
            continue;
        for (let bit = 0; bit < 8; bit++) {
            if (fresh & (1 << bit))
                out.push(byte * 8 + bit);
        }
    }
    return out;
}
const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
export function bitsToBase64Url(bits) {
    let out = "";
    for (let i = 0; i < bits.length; i += 3) {
        const a = bits[i];
        const b = i + 1 < bits.length ? bits[i + 1] : 0;
        const c = i + 2 < bits.length ? bits[i + 2] : 0;
        const n = (a << 16) | (b << 8) | c;
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
        if (i + 1 < bits.length)
            out += B64[(n >> 6) & 63];
        if (i + 2 < bits.length)
            out += B64[n & 63];
    }
    return out;
}
export function base64UrlToBits(s) {
    var _a, _b, _c;
    if (typeof s !== "string")
        return null;
    if (s.length > Math.ceil((SHOWCASE_MAX_SECONDS / 8 / 3) * 4) + 4)
        return null;
    if (s.length % 4 === 1)
        return null;
    const vals = [];
    for (let i = 0; i < s.length; i++) {
        const v = B64.indexOf(s[i]);
        if (v < 0)
            return null;
        vals.push(v);
    }
    const out = new Uint8Array(Math.floor((s.length * 3) / 4));
    let o = 0;
    for (let i = 0; i < vals.length; i += 4) {
        const n = (vals[i] << 18) |
            (((_a = vals[i + 1]) !== null && _a !== void 0 ? _a : 0) << 12) |
            (((_b = vals[i + 2]) !== null && _b !== void 0 ? _b : 0) << 6) |
            ((_c = vals[i + 3]) !== null && _c !== void 0 ? _c : 0);
        if (o < out.length)
            out[o++] = (n >> 16) & 255;
        if (o < out.length && i + 2 < vals.length)
            out[o++] = (n >> 8) & 255;
        if (o < out.length && i + 3 < vals.length)
            out[o++] = n & 255;
    }
    return out;
}
export function completionPct(watchedSeconds, durationSeconds) {
    if (!durationSeconds || durationSeconds <= 0)
        return 0;
    const whole = Math.max(1, Math.floor(durationSeconds));
    return Math.min(100, Math.round((watchedSeconds / whole) * 100));
}
export function formatTimecode(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const ss = sec < 10 ? `0${sec}` : `${sec}`;
    if (h > 0)
        return `${h}:${m < 10 ? `0${m}` : m}:${ss}`;
    return `${m}:${ss}`;
}
export function rewatchedRanges(bits, minSeconds = 5) {
    const out = [];
    let start = -1;
    const total = bits.length * 8;
    for (let i = 0; i <= total; i++) {
        const on = i < total && hasBit(bits, i);
        if (on && start < 0)
            start = i;
        if (!on && start >= 0) {
            if (i - start >= minSeconds)
                out.push({ from: start, to: i });
            start = -1;
        }
    }
    return out.sort((a, b) => b.to - b.from - (a.to - a.from));
}
export function parseChapters(text) {
    const out = [];
    for (const raw of text.split("\n")) {
        const line = raw.trim();
        const m = /^(?:(\d+):)?(\d{1,2}):(\d{2})\s+(.+)$/.exec(line);
        if (!m)
            continue;
        const h = m[1] ? Number(m[1]) : 0;
        const startSeconds = h * 3600 + Number(m[2]) * 60 + Number(m[3]);
        out.push({ startSeconds, title: m[4].trim().slice(0, 120) });
    }
    return out.sort((a, b) => a.startSeconds - b.startSeconds);
}
export function chaptersToText(chapters) {
    return chapters
        .map((c) => `${formatTimecode(c.startSeconds)} ${c.title}`)
        .join("\n");
}
export function sanitiseChapters(raw) {
    if (!Array.isArray(raw) || raw.length > 100)
        return null;
    const out = [];
    for (const c of raw) {
        if (!c || typeof c !== "object")
            return null;
        const { startSeconds, title } = c;
        if (typeof startSeconds !== "number" ||
            !Number.isFinite(startSeconds) ||
            startSeconds < 0 ||
            typeof title !== "string" ||
            !title.trim()) {
            return null;
        }
        out.push({
            startSeconds: Math.floor(startSeconds),
            title: title.trim().slice(0, 120),
        });
    }
    return out.sort((a, b) => a.startSeconds - b.startSeconds);
}
function escapeHtml(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}
export function embedSnippet(args) {
    var _a;
    const base = args.baseUrl.replace(/\/+$/, "");
    const ref = normaliseRef((_a = args.ref) !== null && _a !== void 0 ? _a : null);
    const src = `${base}/e/${encodeURIComponent(args.slug)}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`;
    const ratio = args.width && args.height && args.width > 0 && args.height > 0
        ? `${Math.round(args.width)}/${Math.round(args.height)}`
        : "16/9";
    return [
        `<div style="position:relative;aspect-ratio:${ratio};width:100%;border-radius:16px;overflow:hidden">`,
        `  <iframe`,
        `    src="${escapeHtml(src)}"`,
        `    style="position:absolute;inset:0;width:100%;height:100%;border:0"`,
        `    allow="autoplay; fullscreen; picture-in-picture"`,
        `    allowfullscreen`,
        `    title="${escapeHtml(args.title)}"></iframe>`,
        `</div>`,
    ].join("\n");
}
